import React, { useState, useMemo, useEffect, useRef } from 'react';
import ShieldIcon from '@mui/icons-material/Shield';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import PopOver from '../../components/PopOver/PopOver';
import { Box, Button, Chip, CircularProgress, Grid, Stack, TextField, Tooltip, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { transformQuillListToNested, nestedListStyles } from '../../utils/quillHtmlTransform';
import { format, parseISO } from 'date-fns';
import PhaseProgress from './PhaseProgress';
import { TaskExtended, TaskStatus } from '../../../shared/types/Task';
import { DataFieldsEnum, DataFieldsOptionsEnum, TRMField } from '../../../shared/types/DataField';
import { Link } from 'react-router-dom';
import {
  useGetSepQuery,
  useGetSepTasksQuery,
} from '../../services/sepSlice';
import { useGetUserQuery } from '../../services/usersSlice';
import {
  useGetTaskTemplatesQuery,
} from '../../services/tasksSlice/tasksSlice';
import DataClassificationFields from '../../components/DataClassificationFields';
import APMFields from '../../components/APMFields';
import ModalWidget from '../../components/ModalWidget/ModalWidget';
import './styles.css';

import { NewWorkflowType, SEPPhase, SEPSearchRow, SEPStatus } from '../../../shared/types/SEP';
import { useAccount } from '@azure/msal-react';
import { env } from '../../../shared/env';
import EditSEP from './EditSEP';
import { useGetUserNotesBySepQuery } from '../../services/userNoteSlice/userNoteSlice';
import {
  useGetSepAISummaryQuery,
  useRegenerateSepAISummaryMutation,
  useUpdateSepAISummaryMutation,
} from '../../services/aiSummaryService';
import { useGetAttachmentByIDQuery } from '../../services/attachmentSlice/attachmentSlice';
import SepNotesDrawer from '../../components/SepNotesDrawer/SepNotesDrawer';
import { useGetDataFieldTemplatesQuery } from '../../services/dataFieldsSlice';
import { useGetDepartmentsQuery, useGetDefaultStakeholdersForSEPQuery } from '../../services/departmentSlice/departmentSlice';
import { useGetLatestActivityQuery } from '../../services/activitySlice/activitySlice'
import { GetDataFieldOptionTemplate } from '../../utils/dataFieldOptionTemplate'
import SepAge from '../../../frontend/components/SepTable/SepAge';
import SepTaskHeaderBar from './SepTaskHeaderBar';
import OnTheClockIcon from '../../assets/svg/OnTheClockIcon';
import { spacingTokens } from '../../theme/spacingTokens';
import { typographyTokens, typography } from '../../theme/typographyTokens';
import ClockIcon from '../../../frontend/assets/svg/ClockIcon';
const SEPProgressGraphic = require('../../assets/img/sepProgressGraphic.png');
const RAIProgressGraphic = require('../../assets/img/raiProgressGraphic.png');


interface GroupTasks {
  validation: TaskExtended[];
  initiate: TaskExtended[];
  design: TaskExtended[];
  implement: TaskExtended[];
}
interface SepHeaderProps {
  dataFields: any;
  sepID: number;
  roleList: any;
  completedAt?: Date;
}

const SepHeader = ({ dataFields, sepID, roleList }: SepHeaderProps) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const { data: sep } = useGetSepQuery(sepID);
  const { data: loggedInUser } = useGetUserQuery('me');
  const [sepEdit, setSepEdit] = useState<boolean>(false);
  const [notesOpen, setNotesOpen] = useState<boolean>(false);
  const { data: userNotes = [] } = useGetUserNotesBySepQuery(sepID);
  // Count only regular notes (exclude templates where template=1)
  const sepLevelNotesCount = userNotes.filter((note) => note.template !== 1).length;
  const { data: adminKeyTopics } = useGetDataFieldTemplatesQuery();
  const { data: departments } = useGetDepartmentsQuery();
  const { data: defaultStakeholders } = useGetDefaultStakeholdersForSEPQuery(sepID);
  const { data: latestSepActivity } = useGetLatestActivityQuery(sepID);

  // Account is for azure login, token, tenantId, etc.
  const account = useAccount();
  const tokenId = account?.idTokenClaims?.oid;
  const isAdminUser = env.REACT_APP_LOCAL_DEV_ADMIN === 'true' || (account?.idTokenClaims?.roles?.includes('AuthSuperUser') ?? false);
  const canShowAiSummary = isAdminUser && !!sep && sep.phase !== SEPPhase.knockout;

  const TRMfields = dataFields?.filter((tm: TRMField) => tm.name.startsWith('TRM'))
  const result = TRMfields?.reduce((acc: Record<string, string>, curr: TRMField) => {
    const { name, value, dataFieldOptions } = curr
    if (value) {
      acc[name] = value;
    } else if (Array.isArray(dataFieldOptions)) {
      const selectedOptions = dataFieldOptions
        .filter(opt => opt.selected)
        .map(opt => opt?.value);
      if (selectedOptions.length > 0) {
        acc[curr.name] = selectedOptions.join(", ");
      }
    }
    return acc;
  }, {});

  const sepData = useGetSepQuery(sepID);
  const createdById = sepData.data?.createdBy;
  const creatorId = sepData.data?.creator.id;
  const editPermission =
    roleList && roleList.length
      ? roleList?.includes('AuthSuperUser') ||
        roleList?.includes('AuthSolutionArchitect') ||
        roleList?.includes('AuthPortfolioOwner') ||
        roleList?.includes('AuthSupply') ||
        roleList?.includes('AuthNuclearCyberSecurity') ||
        roleList?.includes('AuthThirdPartySecurity') ||
        roleList?.includes('AuthSecurity') ||
        roleList?.includes('AuthEA') ||
        roleList?.includes('AuthLegal')
        ? true
        : false
      : false;
  const apm = React.useMemo(() => {
    return sep?.apmData ? JSON.parse(sep?.apmData as string) : null;
  }, [sep]);
  const { data: tasks } = useGetSepTasksQuery(sepID);
  const { data: taskTemplates } = useGetTaskTemplatesQuery();
  const groupByPhase: GroupTasks = useMemo(() => {
    const filteredTasks = tasks
      ?.filter((t) => t.enabled)
      .filter((t, index, self) => self.findIndex((task) => task.id === t.id) === index);

    return filteredTasks && filteredTasks.length !== 0
      ? groupBy(filteredTasks, 'phase')
      : [];
  }, [tasks]);

  const sepDesignReview = sep?.workFlowType === NewWorkflowType.sepDesignReview;
  const statusForSummary = ['todo', 'changesRequested', 'inReview'];
  const tasksForSummary = tasks
    ?.filter((t) => {
      return t.enabled && statusForSummary.includes(t.status)
    })
  const getSummary = () => {
    const summary: any[] = [];
    tasksForSummary?.forEach((task, i) => {
      if (task.taskTemplateID) {
        if (task?.assignedUserID === task?.defaultReviewerID) {
          //if (taskTemplateForSummary[i]?.stakeholderSummary) summary.push(taskTemplateForSummary[i]?.stakeholderSummary)
          const stakeholderSummary = taskTemplates?.filter((t) => t.id === task.taskTemplateID)[0]?.stakeholderSummary
          if (stakeholderSummary) summary.push(stakeholderSummary)
        } else {
          //if (taskTemplateForSummary[i]?.requestorSummary) summary.push(taskTemplateForSummary[i]?.requestorSummary)
          const requestorSummary = taskTemplates?.filter((t) => t.id === task.taskTemplateID)[0]?.requestorSummary
          if (requestorSummary) summary.push(requestorSummary)
        }
      } else {
        const dept = departments?.filter((d: any) => d.id === task.departmentID)[0]
        if (task?.assignedUserID === task?.defaultReviewerID) {
          if (!summary.includes(`${dept?.name} reviewing information provided by requestor.`))
            summary.push(`${dept?.name} reviewing information provided by requestor.`)
        } else if (task?.assignedUserID === creatorId) {
          if (!summary.includes(`Requestor gathering information for ${dept?.name}`))
            summary.push(`Requestor gathering information for ${dept?.name}`)
        } else {
          if (!summary.includes(`SME being consulted to support ${dept?.name} Review.`))
            summary.push(`SME being consulted to support ${dept?.name} Review.`)
        }
      }

    })
    return summary
  }
  const getKeyTopics = () => {
    const keyTopicsData = dataFields?.filter((data: any) => data?.name === 'Key Topics');
    const adminkeyTopics = adminKeyTopics?.find((data: any) => data?.name === 'Key Topics');
    const finalResult: { reviewStatement: string; color: string }[] = [];

    const filterAdminKeyTopics = adminkeyTopics && adminkeyTopics?.dataFieldOptions.filter((priorityValue: any) => {
      return priorityValue.colors !== null && priorityValue.priorityTopics !== null;
    });

    const userSelectedKeyTopics = keyTopicsData && keyTopicsData[0]?.dataFieldOptions;

    if (filterAdminKeyTopics && userSelectedKeyTopics) {
      userSelectedKeyTopics.forEach((option: any) => {
        if (option?.selected) {
          const selectedTopic = option?.value;
          filterAdminKeyTopics?.forEach((priorityValue: any) => {
            if (priorityValue?.priorityTopics.trim().replace(/\s+/g, ' ') === selectedTopic) {
              finalResult?.push({ reviewStatement: priorityValue?.reviewStatements, color: priorityValue?.colors });
            }
          });
        }
      });
    }
    return finalResult;
  }

  const workflowData: any = GetDataFieldOptionTemplate(DataFieldsEnum.WORKFLOW_TYPE, sep?.workFlowType);
  const designReview: any = sep?.workFlowType === null && GetDataFieldOptionTemplate(DataFieldsEnum.WORKFLOW_TYPE, DataFieldsOptionsEnum.SEP_DESIGN_REVIEW);
  const powerBiLink = workflowData?.powerbiLink?.replace('{sepID}', sepID)
  const designReviewLink = designReview?.powerbiLink?.replace('{sepID}', sepID)
  const isLinkValid = powerBiLink || designReviewLink;


  const isKeyTopicAISelected: boolean = dataFields && dataFields?.find((data: any) => data?.name === DataFieldsEnum.KEY_TOPICS)?.dataFieldOptions.find((val: any) => val?.value === DataFieldsOptionsEnum.ARTIFICIAL_INTELLIGENCE)?.selected;
  const AIDatafieldObject: any = GetDataFieldOptionTemplate(DataFieldsEnum.KEY_TOPICS, DataFieldsOptionsEnum.ARTIFICIAL_INTELLIGENCE);
  const AIpowerBiLink = isKeyTopicAISelected
    ? AIDatafieldObject?.powerbiLink?.replace('{sepID}', sepID) ?? ''
    : '';

  const replaceStakeholdersSummary = (desc: any, responseData: any) => {
    const placeholderRegex = /{([^{}]+?)stakeholder}/gi;
    const replacePlaceholder = (match: any, placeholderName: any) => {
      const normalizedPlaceholder = normalizeString(placeholderName);
      const matchingDepartment = responseData?.find(
        (item: any) => {
          const normalizedDepartmentName = normalizeString(item?.department?.name);
          return normalizedDepartmentName === normalizedPlaceholder
        });
      return matchingDepartment && matchingDepartment?.user && matchingDepartment?.user?.givenName ? `<b>${matchingDepartment?.user?.givenName} ${matchingDepartment?.user?.surname}</b>` : "<b>User assignment is still pending or not required</b>"
    };
    const normalizeString = (str: any) => {
      return str.toLowerCase().replace(/[^a-z0-9]/g, '')
    }
    const replacedDescriptions = desc?.map((descript: any) => {
      return descript?.replace(placeholderRegex, replacePlaceholder)
    })
    return replacedDescriptions
  }
  const summaryDesc = getSummary()
  const replacedDesc = replaceStakeholdersSummary(summaryDesc, defaultStakeholders)

  const completeValidatePhase =
    tasks?.length !== 0 && groupByPhase.validation !== undefined
      ? (groupBy(groupByPhase.validation, 'status').complete as TaskExtended[])
      : null;
  const completeInitiatePhase =
    tasks?.length !== 0 && groupByPhase.initiate !== undefined
      ? (groupBy(groupByPhase.initiate, 'status').complete as TaskExtended[])
      : null;
  const completeDesignPhase =
    tasks?.length !== 0 && groupByPhase.design !== undefined
      ? (groupBy(groupByPhase.design, 'status').complete as TaskExtended[])
      : null;
  const completeImplementPhase =
    tasks?.length !== 0 && groupByPhase.implement !== undefined
      ? (groupBy(groupByPhase.implement, 'status').complete as TaskExtended[])
      : null;

  const isAllDesignTasksCompleted = groupByPhase?.design && groupByPhase?.design?.filter((task) => task?.phase === 'design')?.every((task) => task?.completedAt !== null);

  function groupBy(objectArray: any[], property: string) {
    return objectArray.reduce((acc: any, obj: any) => {
      const key = obj[property];
      const curGroup = acc[key] ?? [];

      return { ...acc, [key]: [...curGroup, obj] };
    }, {});
  }


  const [showSummary, setshowSummary] = useState<boolean>(false);
  const {
    data: aiSummary,
    error: aiSummaryError,
    isLoading: aiSummaryLoading,
  } = useGetSepAISummaryQuery(sepID, { skip: !sepID || !canShowAiSummary });
  const { data: attachments } = useGetAttachmentByIDQuery(sepID);

  const [regenerateAISummary, { isLoading: aiSummaryRegenerating }] =
    useRegenerateSepAISummaryMutation();
  const [updateAISummary, { isLoading: aiSummarySaving }] = useUpdateSepAISummaryMutation();
  const [editingSummary, setEditingSummary] = useState<boolean>(false);
  const [summaryDraft, setSummaryDraft] = useState<string>('');
  const [showFullSummary, setShowFullSummary] = useState<boolean>(false);
  const [hasSummaryOverflow, setHasSummaryOverflow] = useState<boolean>(false);
  const summaryRef = useRef<HTMLDivElement>(null);

  const aiSummaryText = aiSummary?.summary
    ? aiSummary.summary
    : aiSummaryError
    ? `Error: ${(aiSummaryError as any)?.data?.error || 'Unable to generate summary.'}`
    : '';
  const aiSummaryMutationBusy = aiSummaryRegenerating || aiSummarySaving;
  const aiSummaryBusy = aiSummaryLoading || aiSummaryMutationBusy;

  useEffect(() => {
    setShowFullSummary(false);
  }, [aiSummaryText]);

  useEffect(() => {
    if (aiSummaryBusy) {
      setShowFullSummary(false);
      setHasSummaryOverflow(false);
    }
  }, [aiSummaryBusy]);

  useEffect(() => {
    const summaryElement = summaryRef.current;
    if (!summaryElement || !aiSummaryText || editingSummary || aiSummaryBusy) {
      setHasSummaryOverflow(false);
      return;
    }

    if (showFullSummary) {
      setHasSummaryOverflow(true);
      return;
    }

    setHasSummaryOverflow(summaryElement.scrollHeight > summaryElement.clientHeight + 1);
  }, [aiSummaryText, editingSummary, showFullSummary, aiSummaryBusy]);

  const handleEditSummary = () => {
    setSummaryDraft(aiSummary?.summary ?? '');
    setEditingSummary(true);
  };

  const handleSaveSummary = async () => {
    await updateAISummary({ sepID, summary: summaryDraft });
    setEditingSummary(false);
  };

  const workflowTitle = sep?.workFlowType || 'SEP Design Review';
  const phaseTitle = ((sep as { phase?: string })?.phase === 'knockout' ? 'Draft' : sep?.phase || '').toString();
  const lastSavedAt = latestSepActivity?.[0]?.[0]?.lastUpdate as string | undefined;
  const formattedLastSavedAt = lastSavedAt ? format(parseISO(lastSavedAt), 'Pp') : undefined;

  function handlePhaseClick(): void {
    throw new Error('Function not implemented.');
  }

  return (
    <Box sx={{ m: 0, p: 0 }}>
      {/* SepTaskHeaderBar - Full width (breaks out of container padding) */}
      <Box
        sx={{
          mx: { xs: -2, sm: -3, md: -3 },
          width: { xs: 'calc(100% + 32px)', sm: 'calc(100% + 48px)', md: 'calc(100% + 48px)' }
        }}
      >
        <SepTaskHeaderBar
          phaseLabel={phaseTitle || 'Validation'}
          workflowLabel={workflowTitle}
          sepID={sepID}
          lastSavedLabel={formattedLastSavedAt}
          openMyNotes={() => setNotesOpen(true)}
          sepNotesCount={sepLevelNotesCount}
        />
      </Box>
      <ModalWidget
        isOpen={sepEdit}
        children={
          <EditSEP
            sep={sep}
            dataFields={dataFields}
            onClose={() => setSepEdit(false)}
            isAdminUser={isAdminUser}
          />
        }
        title="Edit SEP Detail"
        onClose={() => setSepEdit(false)}
      />
      {sep && (
        <SepNotesDrawer
          open={notesOpen}
          onClose={() => setNotesOpen(false)}
          row={{
            id: sepID,
            name: sep.name,
            phase: sep.phase,
            workFlowType: sep.workFlowType,
          } as any}
          hideOpenSepButton={true}
        />
      )}
      <Box sx={{ padding: { xs: 2, sm: 3, md: 4 }, backgroundColor: isDarkMode ? theme.palette.background.default : theme.palette.white.main, borderBottom: `1px solid ${theme.palette.divider}` }}>

        {/* Main Content: Left and Right Sections (50% each on md+) */}
        <Grid container spacing={3} sx={{ flexDirection: { xs: 'column', md: 'row' } }}>

          {/* LEFT SECTION (50%) */}
          <Grid item xs={12} md={6}>
            {/* Row 1: SEP Status (left) + Key Topics (right) */}
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, gap: 2, mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Chip
                  variant="outlined"
                  icon={<OnTheClockIcon color={sep?.phase === SEPPhase.complete ? theme.palette.prime.green : theme.palette.prime.blue} />}
                  label={sep?.status === SEPStatus.active ? 'In Progress' : sep?.status}
                  sx={{
                    fontWeight: typographyTokens.fontWeights.semibold,
                    fontSize: typographyTokens.fontSizes.sm,
                    height: 28,
                    borderRadius: '14px',
                    textTransform: 'capitalize',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderColor: sep?.phase === SEPPhase.complete ? theme.palette.prime.green : theme.palette.prime.blue,
                    color: sep?.phase === SEPPhase.complete ? theme.palette.prime.green : theme.palette.prime.blue,
                    '& .MuiChip-label': { pl: 0.5, pr: 1 },
                    '& .MuiChip-icon': { marginLeft: '5px', marginRight: '0px', marginTop: '0px' },
                  }}
                />
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {getKeyTopics()?.map((s, index) => {
                  const style: React.CSSProperties = {
                    backgroundColor: s?.color,
                    color: theme.palette.white.main,
                    display: 'inline-flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    borderRadius: 4,
                    padding: '5px 12px',
                    fontSize: typography.caption.fontSize,
                    wordBreak: 'break-word',
                  };
                  return (
                    <React.Fragment key={index}>
                      {s?.reviewStatement && (
                        <div style={style}>
                          <b>{s?.reviewStatement}</b>
                        </div>
                      )}
                    </React.Fragment>
                  );
                })}
              </Box>
            </Box>

            {/* Row 2: SEP Name */}
            <Box sx={{ mb: 2 }}>
              <Typography sx={{ font: 'normal normal 600 25px/34px Open Sans;', wordWrap: 'break-word', color: isDarkMode ? theme.palette.white.main : theme.palette.content.default }}>
                {sep?.name}
              </Typography>
            </Box>

            {/* AI-Generated Summary — shown directly below the SEP name */}
            {isAdminUser && <Box
              sx={{
                mb: 2,
                p: { xs: 1.5, sm: 2 },
                border: `1px solid ${alpha(theme.palette.prime.blue, isDarkMode ? 0.35 : 0.16)}`,
                borderRadius: 2,
                background: isDarkMode
                  ? `linear-gradient(135deg, ${alpha(theme.palette.prime.blue, 0.12)}, ${alpha(theme.palette.background.paper, 0.72)})`
                  : `linear-gradient(135deg, ${alpha(theme.palette.prime.blue, 0.055)}, ${alpha(theme.palette.white.main, 0.96)})`,
                boxShadow: isDarkMode ? 'none' : `0 4px 16px ${alpha(theme.palette.prime.blue, 0.06)}`,
              }}
            >
              <Stack direction='row' alignItems='center' spacing={1} sx={{ mb: 0.5 }}>
                <AutoAwesomeRoundedIcon sx={{ fontSize: 16, color: theme.palette.prime.blue }} />
                <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.01em', color: theme.palette.prime.blue }}>
                  {aiSummary?.edited ? 'Summary' : 'AI-Generated Summary'}
                </Typography>
                {isAdminUser && !editingSummary && (
                  <>
                    <Button size='small' sx={{ fontSize: 11, minWidth: 0 }} disabled={aiSummaryBusy} onClick={handleEditSummary}>
                      Edit
                    </Button>
                    <Tooltip title='Discards any manual edit and generates a new summary'>
                      <span>
                        <Button size='small' sx={{ fontSize: 11, minWidth: 0 }} disabled={aiSummaryBusy} onClick={() => regenerateAISummary(sepID)}>
                          Regenerate
                        </Button>
                      </span>
                    </Tooltip>
                  </>
                )}
                {aiSummaryMutationBusy && <CircularProgress size={12} />}
              </Stack>
              {sep?.phase === SEPPhase.knockout ? (
                <Typography sx={{ fontSize: 13, lineHeight: 1.7, color: 'text.secondary' }}>
                  AI summary will be available once the SEP moves out of Draft.
                </Typography>
              ) : editingSummary ? (
                <Stack spacing={1} alignItems='flex-start'>
                  <TextField
                    multiline
                    minRows={3}
                    fullWidth
                    size='small'
                    value={summaryDraft}
                    onChange={(e) => setSummaryDraft(e.target.value)}
                    inputProps={{ style: { fontSize: 13, lineHeight: 1.7 } }}
                  />
                  <Stack direction='row' spacing={1}>
                    <Button size='small' variant='contained' disabled={aiSummarySaving || !summaryDraft.trim()} onClick={handleSaveSummary}>
                      Save
                    </Button>
                    <Button size='small' disabled={aiSummarySaving} onClick={() => setEditingSummary(false)}>
                      Cancel
                    </Button>
                  </Stack>
                </Stack>
              ) : aiSummaryLoading ? (
                <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>Loading summary…</Typography>
              ) : (
                <Typography
                  ref={summaryRef}
                  sx={{
                    fontSize: 13,
                    lineHeight: 1.65,
                    color: isDarkMode ? theme.palette.white.main : theme.palette.content.default,
                    display: '-webkit-box',
                    WebkitBoxOrient: 'vertical',
                    WebkitLineClamp: showFullSummary ? 'unset' : 5,
                    overflow: showFullSummary ? 'visible' : 'hidden',
                  }}
                >
                  {aiSummaryText || ''}
                </Typography>
              )}
              {!editingSummary && !aiSummaryBusy && hasSummaryOverflow && !showFullSummary && (
                <Button
                  size='small'
                  endIcon={<ExpandMoreRoundedIcon />}
                  onClick={() => setShowFullSummary(true)}
                  sx={{
                    mt: 1.25,
                    px: 1.25,
                    py: 0.5,
                    minWidth: 0,
                    borderRadius: 1.5,
                    textTransform: 'none',
                    fontSize: 12.5,
                    fontWeight: 700,
                    color: theme.palette.prime.blue,
                    backgroundColor: alpha(theme.palette.prime.blue, isDarkMode ? 0.16 : 0.08),
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.prime.blue, isDarkMode ? 0.24 : 0.14),
                    },
                    '& .MuiButton-endIcon': { ml: 0.25 },
                  }}
                >
                  View full summary
                </Button>
              )}
              {!editingSummary && !aiSummaryBusy && hasSummaryOverflow && showFullSummary && (
                <Button
                  size='small'
                  endIcon={<ExpandMoreRoundedIcon sx={{ transform: 'rotate(180deg)' }} />}
                  onClick={() => setShowFullSummary(false)}
                  sx={{
                    mt: 1.25,
                    px: 1.25,
                    py: 0.5,
                    minWidth: 0,
                    borderRadius: 1.5,
                    textTransform: 'none',
                    fontSize: 12.5,
                    fontWeight: 700,
                    color: theme.palette.prime.blue,
                    backgroundColor: alpha(theme.palette.prime.blue, isDarkMode ? 0.16 : 0.08),
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.prime.blue, isDarkMode ? 0.24 : 0.14),
                    },
                    '& .MuiButton-endIcon': { ml: 0.25 },
                  }}
                >
                  Show less
                </Button>
              )}
              {!editingSummary && (
                <Stack direction='row' alignItems='center' spacing={0.5} sx={{ mt: 1 }}>
                  <ShieldIcon sx={{ color: 'yellow.main', fontSize: 16 }} />
                  <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
                    AI-generated content may be incorrect.
                  </Typography>
                </Stack>
              )}
            </Box>}

            {/* Row 3: SEP Age */}
            <Box sx={{ mb: 2 }}>
              <Typography sx={{ font: 'normal normal normal 14px/20px Open Sans;', fontWeight: typographyTokens.fontWeights.bold, display: 'flex', alignItems: 'center', color: isDarkMode ? theme.palette.white.main : theme.palette.content.default }}>
                {sep?.createdAt && (
                  <>
                    <Stack component='span' sx={{ mr: 1 }}>SEP Age (Days):</Stack>
                    <SepAge sepObject={sep as SEPSearchRow} showPhaseText={true} />
                  </>
                )}
              </Typography>
            </Box>

            {/* Row 4: Edit SEP Header (admin only) */}
            {editPermission && (
              <Box sx={{ mb: 1 }}>
                <Button
                  variant="text"
                  onClick={() => setSepEdit(true)}
                  sx={{ textTransform: 'none', font: 'normal normal 600 14px/20px Open Sans;', p: 0, minWidth: 'auto' }}
                >
                  <i className="fa-solid fa-pencil" style={{ marginRight: 8 }} />
                  Edit SEP Header
                </Button>
              </Box>
            )}

            {/* Row 5: View SEP Details */}
            <Box sx={{ mb: 1 }}>
              <Button
                variant="text"
                component={Link}
                to={`/details/${sepID}`}
                sx={{ textTransform: 'none', font: 'normal normal 600 14px/20px Open Sans;', p: 0, minWidth: 'auto' }}
              >
                <i className="fa-solid fa-file-lines" style={{ marginRight: 8 }} />
                View SEP Details
              </Button>
            </Box>

            {/* Row 6: Show Summary */}
            {getSummary().length > 0 && (
              <Box sx={{ mb: 1 }}>
                <Button
                  variant="text"
                  onClick={() => setshowSummary(!showSummary)}
                  sx={{ textTransform: 'none', font: 'normal normal 600 14px/20px Open Sans;', p: 0, minWidth: 'auto' }}
                >
                  <i className="fa-solid fa-list" style={{ marginRight: 8 }} />
                  {showSummary ? 'Hide Summary' : 'Show Summary'}
                </Button>
              </Box>
            )}

            {/* Row 7: My Notes */}
            {/* <Box sx={{ mb: 1 }}>
              <Button
                variant="text"
                onClick={() => setNotesOpen(true)}
                sx={{ textTransform: 'none', font: 'normal normal 600 14px/20px Open Sans;', p: 0, minWidth: 'auto' }}
              >
                <i className="fa-solid fa-sticky-note" style={{ marginRight: 8 }} />
                My SEP Notes {sepLevelNotesCount > 0 ? `(${sepLevelNotesCount})` : '(0)'}
              </Button>
            </Box> */}
          </Grid>

          {/* RIGHT SECTION (50%) */}
          <Grid item xs={12} md={6}>
            {/* My Request Progress Section */}
            {((designReview?.sepProgressBar && sep?.workFlowType === null) || workflowData?.sepProgressBar) && (
              <Box sx={{
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 2,
                p: 2,
                backgroundColor: isDarkMode ? theme.palette.background.paper : theme.palette.surface.level2,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                gap: 2

              }}>

                {/* Phase Progress Cards */}
                <Grid container spacing={2} sx={{ width: '100%' }}>
                  {sepDesignReview && (
                    <Grid item xs={6} sm={6} md={4} lg={4}>
                      <PhaseProgress
                        title="Validation Phase"
                        sepPhase={groupByPhase.validation !== undefined ? groupByPhase.validation : null}
                        completedTasks={completeValidatePhase}
                        phaseTitle="Validation Phase"
                        onClick={handlePhaseClick}
                        isSEPPhase={sep?.phase === SEPPhase.validation}
                      />
                    </Grid>
                  )}
                  <Grid item xs={6} sm={6} md={sepDesignReview ? 4 : 4} lg={sepDesignReview ? 4 : 4}>
                    <PhaseProgress
                      title="Initiate Phase"
                      sepPhase={groupByPhase.initiate !== undefined ? groupByPhase.initiate : null}
                      completedTasks={completeInitiatePhase}
                      phaseTitle="Initiate Phase"
                      onClick={handlePhaseClick}
                      isSEPPhase={sep?.phase === SEPPhase.initiate}
                    />
                  </Grid>
                  <Grid item xs={6} sm={6} md={sepDesignReview ? 4 : 4} lg={sepDesignReview ? 4 : 4}>
                    <PhaseProgress
                      title="Design Phase"
                      sepPhase={groupByPhase.design !== undefined ? groupByPhase.design : null}
                      completedTasks={completeDesignPhase}
                      phaseTitle="Design Phase"
                      noTasksInImplementPhase={groupByPhase?.implement === undefined && isAllDesignTasksCompleted}
                      onClick={handlePhaseClick}
                      isSEPPhase={sep?.phase === SEPPhase.design}
                    />
                  </Grid>
                  {groupByPhase?.implement && (
                    <Grid item xs={6} sm={6} md={sepDesignReview ? 4 : 4} lg={sepDesignReview ? 4 : 4}>
                      <PhaseProgress
                        title="Implement Phase"
                        sepPhase={groupByPhase.implement !== undefined ? groupByPhase.implement : null}
                        completedTasks={completeImplementPhase}
                        sepFinish={sep?.phase === SEPPhase.complete}
                        phaseTitle="Implement Phase"
                        onClick={handlePhaseClick}
                        isSEPPhase={sep?.phase === SEPPhase.implement}
                      />
                    </Grid>
                  )}
                </Grid>

                {/* You're on the clock section */}
                {tasks && tasks.filter((t) => t.enabled && t.status !== 'complete').length > 0 && (
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <ClockIcon backgroundColor={theme.palette.prime.orange} iconColor={theme.palette.content.white} height={18} width={18} />
                    </Box>
                    <Typography sx={{ fontSize: typographyTokens.fontSizes.sm, ml: 1, color: isDarkMode ? theme.palette.prime.blue : theme.palette.content.default, fontWeight: typographyTokens.fontWeights.semibold }}>
                      {tasks.filter((t) => t.enabled && (t.status === TaskStatus.todo || t.status === TaskStatus.changesRequested || t.status === TaskStatus.inReview) && t.assignee?.id === loggedInUser?.id).length > 0
                        ? `You're on the clock for ${tasks.filter((t) => t.enabled && (t.status === TaskStatus.todo || t.status === TaskStatus.changesRequested || t.status === TaskStatus.inReview) && t.assignee?.id === loggedInUser?.id).length} tasks`
                        : 'No tasks currently assigned to you'}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
            {/* AI Project Manager Section (Placeholder) */}
            {/* <Box sx={{ mt: 3, p: 2, border: `1px dashed ${theme.palette.divider}`, borderRadius: 2, backgroundColor: isDarkMode ? theme.palette.background.paper : '#f9f9f9' }}>
              <Typography sx={{ fontWeight: typographyTokens.fontWeights.bold, fontSize: typographyTokens.fontSizes.body, mb: 1, display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
                AI Project Manager
              </Typography>
              <Typography sx={{ fontSize: typographyTokens.fontSizes.caption, color: '#7E8083', textAlign: { xs: 'left', md: 'right' } }}>
                Coming soon - AI-powered project management features will be available here.
              </Typography>
            </Box> */}
          </Grid>
        </Grid>

        {/* Summary Section (Collapsible) */}
        {showSummary && (
          <Box sx={{ border: '1px solid gray', my: 3, p: 3, borderRadius: 2, background: isDarkMode ? theme.palette.background.default : theme.palette.content.white, boxShadow: 1, width: '100%', color: isDarkMode ? theme.palette.prime.blue : theme.palette.content.default }}>
            <b>Summary</b>
            <br />
            <Box component="ul" sx={{ margin: 0, paddingLeft: '18px', ...nestedListStyles }}>
              {replacedDesc.map((s: any, index: any) => (
                <li key={index} dangerouslySetInnerHTML={{ __html: transformQuillListToNested(s ?? '') }} />
              ))}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default SepHeader;
